import { useEffect, useMemo, useState } from 'react';
import { fetchCatalogResources } from '../services/resourceCatalogApi';

const BATCH = 40;

/**
 * Loads catalog topic tags for WordPress post ids (used to derive place interests).
 * ready is true after the fetch finishes, even when the catalog is empty or unavailable.
 */
export function useCatalogTopicsByWpId(wpIds: number[]) {
  const key = useMemo(
    () => [...new Set(wpIds)].filter((id) => id > 0).sort((a, b) => a - b).join(','),
    [wpIds]
  );
  const [topicsByWpId, setTopicsByWpId] = useState<Map<number, string[]>>(() => new Map());
  const [ready, setReady] = useState(!key);

  useEffect(() => {
    if (!key) {
      setTopicsByWpId(new Map());
      setReady(true);
      return;
    }

    const ids = key.split(',').map((value) => Number(value));
    let cancelled = false;
    setReady(false);

    const load = async () => {
      const next = new Map<number, string[]>();
      try {
        for (let index = 0; index < ids.length; index += BATCH) {
          const slice = ids.slice(index, index + BATCH);
          const params = new URLSearchParams({
            wpIds: slice.join(','),
            perPage: String(slice.length),
          });
          const body = await fetchCatalogResources(params);
          for (const row of body.data) {
            next.set(row.wpId, row.topics ?? []);
          }
          if (cancelled) return;
          setTopicsByWpId(new Map(next));
        }
      } catch {
        if (!cancelled) setTopicsByWpId(new Map());
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { topicsByWpId, ready };
}
